export const FARM_GENERATION_DEFAULT = 1;

export function normalizeGenerationValue(value, fallback = FARM_GENERATION_DEFAULT) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback;
}

export function getActiveFarmGeneration(farm) {
  if (!farm || typeof farm !== 'object') return FARM_GENERATION_DEFAULT;
  return normalizeGenerationValue(
    farm.activeDataGeneration ?? farm.farmDataGeneration ?? farm.dataGeneration ?? farm.generation,
    FARM_GENERATION_DEFAULT
  );
}

export function getFarmGenerationStartedAt(farm) {
  return String(farm?.generationStartedAt || farm?.generationChangedAt || farm?.createdAt || '').trim();
}

export function getFarmGenerationInfo(farm) {
  const activeDataGeneration = getActiveFarmGeneration(farm);
  return {
    activeDataGeneration,
    generationStartedAt: getFarmGenerationStartedAt(farm),
    generationStartedBy: String(farm?.generationStartedBy || farm?.generationChangedBy || '').trim(),
    generationResetAt: String(farm?.generationResetAt || '').trim(),
    generationResetBy: String(farm?.generationResetBy || '').trim(),
    generationResetReason: String(farm?.generationResetReason || '').trim(),
    previousGenerations: Array.isArray(farm?.previousGenerations) ? farm.previousGenerations : [],
  };
}

export function createNextFarmGenerationRecord(farm, { actor = 'admin', reason = '', now = new Date().toISOString() } = {}) {
  const currentGeneration = getActiveFarmGeneration(farm);
  const nextGeneration = currentGeneration + 1;
  const previousGenerations = Array.isArray(farm?.previousGenerations) ? [...farm.previousGenerations] : [];
  const previousStartedAt = getFarmGenerationStartedAt(farm);
  previousGenerations.push({
    generation: currentGeneration,
    startedAt: previousStartedAt || farm?.createdAt || '',
    endedAt: now,
    endedBy: actor || 'admin',
    resetReason: String(reason || '').trim(),
  });
  return {
    ...farm,
    activeDataGeneration: nextGeneration,
    farmDataGeneration: nextGeneration,
    generationStartedAt: now,
    generationStartedBy: actor || 'admin',
    generationChangedAt: now,
    generationChangedBy: actor || 'admin',
    generationResetAt: now,
    generationResetBy: actor || 'admin',
    generationResetReason: String(reason || '').trim(),
    previousGenerations,
  };
}

export function getRecordGeneration(record, fallbackGeneration = FARM_GENERATION_DEFAULT) {
  if (!record || typeof record !== 'object') return normalizeGenerationValue(fallbackGeneration);
  return normalizeGenerationValue(
    record.dataGeneration ?? record.farmDataGeneration ?? record.generation ?? record._dataGeneration,
    normalizeGenerationValue(fallbackGeneration)
  );
}

export function stampRecordGeneration(record, farm, options = {}) {
  const generation = normalizeGenerationValue(options.generation ?? getActiveFarmGeneration(farm));
  const now = options.now || new Date().toISOString();
  return {
    ...(record || {}),
    dataGeneration: generation,
    farmDataGeneration: generation,
    generationCapturedAt: record?.generationCapturedAt || now,
    generationSource: record?.generationSource || options.source || 'active-farm',
  };
}

export function isRecordInActiveFarmGeneration(record, farm) {
  const activeGeneration = getActiveFarmGeneration(farm);
  return getRecordGeneration(record, FARM_GENERATION_DEFAULT) === activeGeneration;
}

export function hasFarmGenerationChanged(knownFarm, currentFarm) {
  if (!knownFarm?.id || !currentFarm?.id) return false;
  if (String(knownFarm.id) !== String(currentFarm.id)) return false;
  return getActiveFarmGeneration(currentFarm) > getActiveFarmGeneration(knownFarm);
}


export const FARM_GENERATION_OPERATIONAL_COLLECTIONS = new Set([
  "orders",
  "services",
  "thirdPartyServices",
  "occurrences",
  "inspections",
  "vet",
  "stock",
  "stockMovements",
  "invoices",
  "financial",
  "financialClosingLocks",
  "machinery",
  "fueling",
  "machineryMaintenance",
  "pastures",
  "poi",
  "animals",
  "animalLots",
  "animalServiceNutrition",
  "animalFoalNutrition",
  "lotIndividualizations",
  "microchipAudit",
  "birdLotsNutrition",
  "harasEquines",
  "harasStalls",
  "harasSchedule",
  "harasNutrition",
  "harasWeights",
  "checklists",
  "notifications",
]);

export function isFarmGenerationOperationalCollection(collection) {
  return FARM_GENERATION_OPERATIONAL_COLLECTIONS.has(String(collection || ""));
}

export function getStableRecordKey(record) {
  if (!record || typeof record !== "object") return "";
  return String(record.clientId || record.id || record.registryUid || record.recordNumber || "").trim();
}

export function isRecordVisibleInActiveFarmGeneration(record, farm, collection) {
  if (!isFarmGenerationOperationalCollection(collection)) return true;
  return isRecordInActiveFarmGeneration(record, farm);
}

export function filterFarmCollectionByActiveGeneration(list, farm, collection) {
  const raw = Array.isArray(list) ? list : [];
  if (!isFarmGenerationOperationalCollection(collection)) return raw;
  return raw.filter((item) => isRecordInActiveFarmGeneration(item, farm));
}

export function mergeVisibleFarmCollectionWithHiddenGenerationRecords(nextVisibleList, previousRawList, farm, collection) {
  const nextVisible = Array.isArray(nextVisibleList) ? nextVisibleList : [];
  const previousRaw = Array.isArray(previousRawList) ? previousRawList : [];
  if (!isFarmGenerationOperationalCollection(collection)) return nextVisible;
  const nextKeys = new Set();
  for (const item of nextVisible) {
    const key = getStableRecordKey(item);
    if (key) nextKeys.add(key);
  }
  const hidden = previousRaw.filter((item) => {
    const key = getStableRecordKey(item);
    if (key && nextKeys.has(key)) return false;
    return !isRecordInActiveFarmGeneration(item, farm);
  });
  return [...hidden, ...nextVisible];
}

export function stampFarmCollectionGenerations(nextList, prevList, farm, options = {}) {
  const collection = String(options.collection || "");
  if (!isFarmGenerationOperationalCollection(collection)) return Array.isArray(nextList) ? nextList : [];
  const activeGeneration = normalizeGenerationValue(options.generation ?? getActiveFarmGeneration(farm));
  const now = options.now || new Date().toISOString();
  const source = options.source || "active-farm";
  const prevGenerationByKey = new Map();
  for (const prev of Array.isArray(prevList) ? prevList : []) {
    const key = getStableRecordKey(prev);
    if (!key) continue;
    prevGenerationByKey.set(key, getRecordGeneration(prev, FARM_GENERATION_DEFAULT));
  }
  return (Array.isArray(nextList) ? nextList : []).map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return item;
    const existingGeneration = item.dataGeneration ?? item.farmDataGeneration ?? item.generation ?? item._dataGeneration;
    const key = getStableRecordKey(item);
    const prevGeneration = key && prevGenerationByKey.has(key) ? prevGenerationByKey.get(key) : null;
    const preservedGeneration = prevGeneration != null && prevGeneration === activeGeneration ? prevGeneration : null;
    const generation = existingGeneration != null
      ? normalizeGenerationValue(existingGeneration, activeGeneration)
      : normalizeGenerationValue(preservedGeneration || activeGeneration);
    return {
      ...item,
      dataGeneration: generation,
      farmDataGeneration: generation,
      generationCapturedAt: item.generationCapturedAt || now,
      generationSource: item.generationSource || source,
    };
  });
}

export const FARM_GENERATION_CONFLICT_COLLECTION = "generationConflicts";

export function getFarmGenerationConflictKey({ collection = "", record = null, recordKey = "", recordGeneration = null, activeGeneration = null } = {}) {
  const key = String(recordKey || getStableRecordKey(record) || "").trim();
  const fromGen = normalizeGenerationValue(recordGeneration ?? getRecordGeneration(record), FARM_GENERATION_DEFAULT);
  const toGen = normalizeGenerationValue(activeGeneration, FARM_GENERATION_DEFAULT);
  return [String(collection || "unknown").trim() || "unknown", key || "sem-id", `g${fromGen}`, `to${toGen}`].join(":");
}

export function shouldCreateGenerationConflict(record, farm, collection) {
  if (!isFarmGenerationOperationalCollection(collection)) return false;
  if (String(collection || "") === "notifications") return false;
  if (String(collection || "") === FARM_GENERATION_CONFLICT_COLLECTION) return false;
  if (!record || typeof record !== "object" || Array.isArray(record)) return false;
  const activeGeneration = getActiveFarmGeneration(farm);
  const recordGeneration = getRecordGeneration(record, FARM_GENERATION_DEFAULT);
  if (recordGeneration >= activeGeneration) return false;
  const startedAt = Date.parse(getFarmGenerationStartedAt(farm) || "");
  if (!Number.isFinite(startedAt)) return false;
  const capturedAt = Date.parse(record?.generationCapturedAt || record?.updatedAt || record?.createdAt || "");
  if (!Number.isFinite(capturedAt)) return false;
  if (capturedAt < startedAt) return false;
  const source = String(record?.generationSource || "").toLowerCase();
  const staleReason = String(record?.generationStaleReason || record?._generationStaleReason || "").toLowerCase();
  return source.includes("offline") || source.includes("legacy") || source.includes("stale") || staleReason.includes("offline") || staleReason.includes("stale");
}

export function buildGenerationConflictRecord({ farm, collection, record, actor = "sistema", deviceId = "", now = new Date().toISOString() } = {}) {
  const activeGeneration = getActiveFarmGeneration(farm);
  const recordGeneration = getRecordGeneration(record, FARM_GENERATION_DEFAULT);
  const recordKey = getStableRecordKey(record);
  const conflictKey = getFarmGenerationConflictKey({ collection, record, recordKey, recordGeneration, activeGeneration });
  return {
    id: `gen_conflict_${conflictKey}`.replace(/[^a-zA-Z0-9_.:-]/g, "_"),
    clientId: `gen_conflict_${conflictKey}`.replace(/[^a-zA-Z0-9_.:-]/g, "_"),
    conflictKey,
    type: "generation_conflict",
    status: "pending",
    farmId: String(farm?.id || "").trim(),
    farmName: String(farm?.name || "").trim(),
    collection: String(collection || "").trim(),
    entityType: String(collection || "").trim(),
    entityId: String(record?.id || record?.clientId || "").trim(),
    entityClientId: String(record?.clientId || "").trim(),
    entityRecordNumber: String(record?.recordNumber || record?.orderNumber || record?.code || "").trim(),
    originGeneration: recordGeneration,
    activeGeneration,
    generationCapturedAt: String(record?.generationCapturedAt || "").trim(),
    generationSource: String(record?.generationSource || "").trim(),
    detectedAt: now,
    detectedBy: actor || "sistema",
    deviceId: String(deviceId || "").trim(),
    title: String(record?.title || record?.name || record?.description || record?.summary || record?.subject || "Registro operacional").slice(0, 140),
    preview: JSON.stringify(record || {}).slice(0, 1200),
    message: "A fazenda foi reiniciada enquanto este dispositivo estava offline. Os dados lançados pertencem à geração anterior. Entre em contato com o administrador para a adequação dos dados para a nova geração.",
    createdAt: now,
    createdBy: actor || "sistema",
    updatedAt: now,
    updatedBy: actor || "sistema",
  };
}
